import type {
  ArmyDeploymentTargetView,
  CommanderPlacementTargetView,
  CommandViewModel,
  LaneView,
  LaneViewId,
  SupplyViewModel,
} from "../presentation/game-view-model";

interface LaneStatusStripProps {
  lanes: LaneView[];
  selectedLaneId: LaneViewId | undefined;
  legalLandTargetIds?: readonly LaneViewId[];
  landSelectionActive?: boolean;
  supply?: SupplyViewModel;
  command?: CommandViewModel;
  commanderTargets?: readonly CommanderPlacementTargetView[];
  commanderSelectionActive?: boolean;
  armyTargets?: readonly ArmyDeploymentTargetView[];
  armySelectionActive?: boolean;
  onSelectLane: (laneId: LaneViewId) => void;
}

function controlLabel(control: number, maximum: number, hasLand: boolean) {
  return hasLand ? `${control}/${maximum}` : "—";
}

export function LaneStatusStrip({
  lanes,
  selectedLaneId,
  legalLandTargetIds = [],
  landSelectionActive = false,
  supply,
  command,
  commanderTargets = [],
  commanderSelectionActive = false,
  armyTargets = [],
  armySelectionActive = false,
  onSelectLane,
}: LaneStatusStripProps) {
  return (
    <nav className="lane-strip" aria-label="Battlefield lanes">
      {lanes.map((lane) => {
        const selected = lane.laneId === selectedLaneId;
        const legalLandTarget = legalLandTargetIds.includes(lane.laneId);
        const laneSupply = supply?.lanes.find((candidate) => candidate.laneId === lane.laneId);
        const laneCommand = command?.lanes.find((candidate) => candidate.laneId === lane.laneId);
        const commanderTarget = commanderTargets.find(
          (candidate) => candidate.laneId === lane.laneId,
        );
        const legalCommanderTarget = commanderTarget?.legal === true;
        const armyTarget = armyTargets.find((candidate) => candidate.laneId === lane.laneId);
        const legalArmyTarget = armyTarget?.legal === true;
        const supplyShortfall =
          supply?.status === "awaiting-discard" &&
          supply.unsustainableLaneIds.includes(lane.laneId);
        const player = lane.sides["player-1"];
        const opponent = lane.sides["player-2"];
        const resultLabel =
          lane.result === "active"
            ? "Active"
            : lane.result === "player-1-won"
              ? "You claimed"
              : "Opponent claimed";

        return (
          <button
            key={lane.laneId}
            className="lane-chip"
            type="button"
            aria-pressed={selected}
            data-selected={selected}
            data-legal-target={legalLandTarget || legalCommanderTarget || legalArmyTarget}
            data-commander-target={
              commanderSelectionActive
                ? legalCommanderTarget
                  ? commanderTarget.actionType
                  : "illegal"
                : "none"
            }
            data-army-target={
              armySelectionActive ? (legalArmyTarget ? "deploy-army" : "illegal") : "none"
            }
            data-supply-status={
              supplyShortfall
                ? "shortfall"
                : supply?.status === "awaiting-discard"
                  ? "sustainable"
                  : supply?.status
            }
            data-command-actions={laneCommand?.legalChoices.length ?? 0}
            data-testid={`lane-${lane.laneId}`}
            onClick={() => onSelectLane(lane.laneId)}
          >
            <span className="lane-chip__name">{lane.label}</span>
            <span className="lane-chip__control" aria-label={`Opponent Control ${resultLabel}`}>
              <span aria-hidden="true">▲</span>
              {controlLabel(opponent.landControl, opponent.landMaxControl, opponent.hasLand)}
            </span>
            <span className="lane-chip__control" aria-label={`Your Control ${resultLabel}`}>
              <span aria-hidden="true">▼</span>
              {controlLabel(player.landControl, player.landMaxControl, player.hasLand)}
            </span>
            <span className="lane-chip__stage" data-testid={`lane-${lane.laneId}-your-stage`}>
              {player.landStage === undefined ? "No Land" : `Your Stage ${player.landStage}`}
            </span>
            <span className="lane-chip__result">{resultLabel}</span>
            {landSelectionActive ? (
              <span className="lane-chip__legality">
                {legalLandTarget ? "Legal Land target" : "Not a legal Land target"}
              </span>
            ) : null}
            {commanderSelectionActive ? (
              <span className="lane-chip__legality">
                {legalCommanderTarget
                  ? commanderTarget.actionType === "deploy-commander"
                    ? "Deploy target"
                    : "Replace target"
                  : "Not a legal Commander target"}
              </span>
            ) : null}
            {armySelectionActive ? (
              <span className="lane-chip__legality">
                {legalArmyTarget ? "Deploy Army target" : "Not a legal Army target"}
              </span>
            ) : null}
            {supply?.status === "awaiting-discard" && laneSupply !== undefined ? (
              <span className="lane-chip__supply">
                {supplyShortfall ? "Supply shortfall" : "Supply sustainable"}
              </span>
            ) : null}
            {command?.status === "available" &&
            laneCommand !== undefined &&
            !commanderSelectionActive &&
            !armySelectionActive ? (
              <span className="lane-chip__command">
                {laneCommand.legalChoices.length === 0
                  ? "No disband choice"
                  : `${laneCommand.legalChoices.length} disband choice${laneCommand.legalChoices.length === 1 ? "" : "s"}`}
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}
